import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useNavigation } from 'expo-router';
import { Ellipsis, EllipsisVertical, List, X } from 'lucide-react-native';
import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { Platform } from 'react-native';

import { Header } from '@/components/header';
import { HeaderItemRow } from '@/components/headerItems/HeaderItemRow';
import type { HeaderItem } from '@/components/headerItems/types';
import { useLiquidGlass } from '@/components/ui/glass';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { useColors } from '@/hooks/useColors';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';

/**
 * The Ask screens' title and controls, described once through `useAskHeader`
 * and handed to the app's shared `Header` as items, which iOS draws as system
 * bar buttons and Android and compact web as app bar actions. Desktop web draws them as
 * ordinary screen content instead, below the standard `NavBar`: a real
 * navigator header there would replace the app's chrome rather than sit
 * inside it, and desktop already has room for both.
 */
const IS_IOS = Platform.OS === 'ios';

export type AskHeaderConfig = {
  title: string;
  /** A saved topic's own title, which runs long; the feature name keeps the bar's full size. */
  isTopicTitle: boolean;
  canManage: boolean;
  /** Whether the latest turn has a summary for the options menu to open. */
  hasSummary: boolean;
  /** Compact layouts open history from the header; wider ones show it as a column. */
  showsHistoryButton: boolean;
  onShowHistory: () => void;
  onShowSummary: () => void;
  onRename: () => void;
  onDelete: () => void;
  onClose: () => void;
};

type AskHeaderData = Pick<
  AskHeaderConfig,
  'title' | 'isTopicTitle' | 'canManage' | 'hasSummary' | 'showsHistoryButton'
>;

const AskHeaderContext = createContext<Dispatch<SetStateAction<AskHeaderConfig | null>> | null>(
  null
);

/**
 * Whether the native header floats transparently over the thread. Only where
 * glass is drawn and the screen is one column: the scroll edge then blurs the
 * answers beneath the bar. The wider layout's side columns do not scroll, so
 * they would sit under a transparent bar with nothing to separate them.
 *
 * The title stays the system's own, one line and truncated. The scroll edge
 * effect keeps only a system title legible over the thread; a custom title
 * view gets no such treatment and was drawn straight over answers. A system
 * title also cross-fades when a result pushes on top, where a custom one
 * slides away with the screen.
 */
export function useIsAskHeaderTransparent(): boolean {
  const hasGlass = useLiquidGlass();

  const isCompact = useIsCompactLayout();

  return IS_IOS && hasGlass && isCompact;
}

/**
 * The header's controls as items: history in the leading slot on compact
 * layouts, then the topic's options and Close in the trailing one. Every item
 * is always listed and hidden when it does not apply, so the native bar keeps
 * the same children as a conversation gains a summary or the reader's rights
 * change.
 *
 * The options menu holds the summary, rename and delete behind one overflow
 * control. Android's overflow control is the vertical ellipsis.
 */
function getAskHeaderItems(config: AskHeaderConfig): { left: HeaderItem[]; right: HeaderItem[] } {
  return {
    left: [
      {
        type: 'button',
        key: 'history',
        label: 'Show your previous conversations',
        icon: { sf: 'list.bullet', lucide: List },
        isHidden: !config.showsHistoryButton,
        onPress: config.onShowHistory,
      },
    ],
    right: [
      {
        type: 'menu',
        key: 'options',
        label: 'Topic options',
        icon: {
          sf: 'ellipsis',
          lucide: Platform.OS === 'android' ? EllipsisVertical : Ellipsis,
        },
        isHidden: !config.hasSummary && !config.canManage,
        actions: [
          {
            key: 'summary',
            label: 'Summary',
            sfIcon: 'list.clipboard',
            isHidden: !config.hasSummary,
            onPress: config.onShowSummary,
          },
          {
            key: 'rename',
            label: 'Rename',
            sfIcon: 'pencil',
            isHidden: !config.canManage,
            onPress: config.onRename,
          },
          {
            key: 'delete',
            label: 'Delete',
            sfIcon: 'trash',
            isDestructive: true,
            isHidden: !config.canManage,
            onPress: config.onDelete,
          },
        ],
      },
      {
        type: 'button',
        key: 'close',
        label: 'Close Research',
        icon: { sf: 'xmark', lucide: X },
        onPress: config.onClose,
      },
    ],
  };
}

/**
 * A topic's generated title sits one step below the bar's own title size and
 * may take a second line, so it reads in full more often before it truncates.
 * Desktop web only, where the title is drawn as content. The navigator's header
 * uses the bar's own title everywhere else.
 */
function AskHeaderTitle({ title, isTopicTitle }: { title: string; isTopicTitle: boolean }) {
  if (!isTopicTitle) {
    return (
      <Heading size="md" numberOfLines={1} className="text-typography-900">
        {title}
      </Heading>
    );
  }

  return (
    <Heading numberOfLines={2} className="text-center text-[15px] leading-5 text-typography-900">
      {title}
    </Heading>
  );
}

/**
 * The navigator's own header, through the app's shared `Header`, on every
 * layout but desktop web. `headerShown` is threaded through rather than fixed,
 * so desktop web can turn this off live as the viewport crosses `md` — see
 * `AskHeaderHost` for why it can't just unmount this instead.
 *
 * The title is the bar's own on every platform, one line and truncated, and
 * the bar takes the thread's fill, floating where glass is drawn; see
 * `useIsAskHeaderTransparent`.
 */
function AskStackHeader({
  config,
  headerShown,
}: {
  config: AskHeaderConfig;
  headerShown: boolean;
}) {
  const { colors } = useColors();

  const isCompact = useIsCompactLayout();

  const items = getAskHeaderItems(config);

  return (
    <Header
      headerShown={headerShown}
      headerShadowVisible
      contentScrollsUnderHeader={isCompact}
      statusBarBackgroundColor={colors.background[50]}
      headerStyle={{ backgroundColor: colors.background[50] }}
      title={config.title}
      leftItems={items.left}
      rightItems={items.right}
    />
  );
}

/**
 * The header's desktop-web presentation: an ordinary content row below the
 * standard `NavBar`, rather than a Stack-navigator header taking its place.
 * Keeps the three-column row and height `header.web.tsx` draws for this same
 * header's compact-web presentation, so nothing jumps as the viewport crosses
 * `md`.
 *
 * Unlike that header it is not capped to a centered content width. The screen
 * below runs edge to edge in three columns, so a centered row would leave the
 * close control floating over the conversation instead of at the edge of the
 * detail pane. History already has its own sidebar column at this width, so
 * the left column is empty; `config.showsHistoryButton` is `false` on desktop.
 */
function AskDesktopHeaderBar({ config }: { config: AskHeaderConfig }) {
  return (
    <HStack className="min-h-16 w-full items-center justify-center border-b border-outline-200">
      <HStack className="w-full items-center px-4 md:px-6">
        <HStack className="min-w-0 flex-1" />

        <HStack className="min-w-0 flex-1 items-center justify-center overflow-hidden px-2">
          <AskHeaderTitle title={config.title} isTopicTitle={config.isTopicTitle} />
        </HStack>

        <HStack className="min-w-0 flex-1 items-center justify-end">
          <HeaderItemRow items={getAskHeaderItems(config).right} />
        </HStack>
      </HStack>
    </HStack>
  );
}

/**
 * Hosts the Ask screens' navigation header. Mounted in the Ask layout, not the
 * screen, because the screens render in a `Slot`: header options and toolbars
 * declared from inside one apply to the slot's own route, which has no header,
 * and never reach the stack that draws it. The layout is the stack's screen,
 * so what it declares lands on the real header.
 *
 * The screen owns the conversation, so it describes the header through
 * `useAskHeader` and this renders it.
 */
export function AskHeaderHost({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AskHeaderConfig | null>(null);

  const navigation = useNavigation();

  const isCompact = useIsCompactLayout();

  const isDesktopWeb = isWeb && !isCompact;

  /*
   * A declared header outlives its declaration: expo-router applies screen
   * options through `setOptions` and never reverts them on unmount. The layout
   * swaps this host out for its spinner and unavailable states, so without
   * this the route would keep the last conversation's title and controls
   * above a screen that has none.
   */
  useLayoutEffect(() => () => navigation.setOptions({ headerShown: false }), [navigation]);

  return (
    <AskHeaderContext.Provider value={setConfig}>
      {config ? <AskStackHeader config={config} headerShown={!isDesktopWeb} /> : null}

      {config && isDesktopWeb ? <AskDesktopHeaderBar config={config} /> : null}

      {children}
    </AskHeaderContext.Provider>
  );
}

/**
 * Describes the screen's header to the host. Only the data re-publishes it;
 * the handlers are read through a ref at press time, so a screen passing new
 * closures every render does not rebuild the native bar each time.
 */
export function useAskHeader(config: AskHeaderConfig) {
  const setConfig = useContext(AskHeaderContext);

  const handlers = useRef(config);

  handlers.current = config;

  const { title, isTopicTitle, canManage, hasSummary, showsHistoryButton } = config;

  /* Before paint, so the screen does not draw a frame without its header and then jump. */
  useLayoutEffect(() => {
    if (!setConfig) {
      return;
    }

    const data: AskHeaderData = {
      title,
      isTopicTitle,
      canManage,
      hasSummary,
      showsHistoryButton,
    };

    setConfig({
      ...data,
      onShowHistory: () => handlers.current.onShowHistory(),
      onShowSummary: () => handlers.current.onShowSummary(),
      onRename: () => handlers.current.onRename(),
      onDelete: () => handlers.current.onDelete(),
      onClose: () => handlers.current.onClose(),
    });

    return () => setConfig(null);
  }, [setConfig, title, isTopicTitle, canManage, hasSummary, showsHistoryButton]);
}
